// src/admin/CreateOrganizationDialog.tsx
/**
 * "Create organization" (spec §7). `CreateOrganizationDialogView` is pure and renders under the
 * `node` vitest environment; `CreateOrganizationDialog` owns the form state, the per-submit
 * idempotency key, the API call, focus management (the first invalid field after a failed submit,
 * the provider on a 422, the opener on close) and the navigation to the new org's Users tab.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { adminApi } from '../services/adminApi';
import { getOrgDetailUrl } from './adminNavigation';
import {
  CREATE_IN_PROGRESS_RETRY_ATTEMPTS,
  EMPTY_CREATE_ORGANIZATION_FORM,
  IDENTITY_PROVIDER_OPTIONS,
  clearFieldError,
  createWithInProgressRetry,
  firstInvalidField,
  keepRequestIdAfterFailure,
  mapCreateOrganizationFailure,
  nextDisplayName,
  normalizeWorkEmailDomain,
  readApiFailure,
  submissionFor,
  validateCreateOrganizationForm,
  type CreateOrganizationFailure,
  type CreateOrganizationFieldErrors,
  type CreateOrganizationFormValues,
  type CreateSubmission,
} from './orgProvisioning';
import { useDialogEscape } from './useDialogEscape';

const LABEL_CLASS = 'mb-1 block text-sm font-semibold';
const FIELD_ERROR_CLASS = 'mt-1 text-sm text-destructive';
const ALERT_CLASS = 'rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive';

/** The inputs that can carry a validation error (the provider radio cannot). */
export type CreateOrganizationTextField = 'name' | 'workEmailDomain' | 'billingEmail';

export type CreateOrganizationFieldRefs = Partial<Record<CreateOrganizationTextField, RefObject<HTMLInputElement>>>;

export interface CreateOrganizationDialogViewProps {
  values: CreateOrganizationFormValues;
  fieldErrors: CreateOrganizationFieldErrors;
  failure: CreateOrganizationFailure | null;
  submitting: boolean;
  /** The attempt about to run while a 409 request_in_progress is being waited out; null otherwise. */
  retrying: number | null;
  onChange: <K extends keyof CreateOrganizationFormValues>(field: K, value: CreateOrganizationFormValues[K]) => void;
  onSubmit: () => void;
  /** Re-submits after the in-progress retries ran out; that failure dropped the key, so this mints a new one. */
  onRetry: () => void;
  onClose: () => void;
  /** Attached to the text inputs so a failed submit can focus the first invalid one. */
  fieldRefs?: CreateOrganizationFieldRefs;
  /** Attached to the checked identity-provider radio so a 422 can move focus there. */
  providerFieldRef?: RefObject<HTMLInputElement>;
}

interface TextFieldProps {
  field: CreateOrganizationTextField;
  id: string;
  label: string;
  value: string;
  error: string | undefined;
  inputRef: RefObject<HTMLInputElement> | undefined;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  autoFocus?: boolean;
  autoCapitalize?: string;
  spellCheck?: boolean;
  disabled?: boolean;
}

// One labelled text input; when invalid it carries aria-invalid and describes itself by the error
// line's id so the message is announced with the field.
function TextField({ field, id, label, value, error, inputRef, onChange, ...inputProps }: TextFieldProps) {
  const errorId = `create-org-${field}-error`;
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>{label}</label>
      <Input
        id={id}
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...inputProps}
      />
      {error && <p id={errorId} className={FIELD_ERROR_CLASS}>{error}</p>}
    </div>
  );
}

export function CreateOrganizationDialogView({
  values,
  fieldErrors,
  failure,
  submitting,
  retrying,
  onChange,
  onSubmit,
  onRetry,
  onClose,
  fieldRefs,
  providerFieldRef,
}: CreateOrganizationDialogViewProps) {
  return (
    <Modal open onOverlayClick={() => { if (!submitting) onClose(); }}>
      <Modal.Content maxWidth="xl" aria-labelledby="create-org-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="create-org-title" className="text-lg font-medium text-foreground">Create organization</h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <form
            id="create-org-form"
            className="flex flex-col gap-4"
            onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          >
            <TextField
              field="name"
              id="create-org-name"
              label="Company name"
              value={values.name}
              error={fieldErrors.name}
              inputRef={fieldRefs?.name}
              onChange={(value) => onChange('name', value)}
              placeholder="Acme"
              autoFocus
              disabled={submitting}
            />

            <div>
              <label htmlFor="create-org-display-name" className={LABEL_CLASS}>Display name</label>
              <Input
                id="create-org-display-name"
                value={values.displayName}
                onChange={(e) => onChange('displayName', e.target.value)}
                placeholder="Shown in the app; defaults to the company name"
                disabled={submitting}
              />
            </div>

            <fieldset>
              <legend className={LABEL_CLASS}>Identity provider</legend>
              <div className="flex flex-wrap gap-4">
                {IDENTITY_PROVIDER_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="radio"
                      name="identityProvider"
                      value={option.value}
                      checked={values.identityProvider === option.value}
                      onChange={() => onChange('identityProvider', option.value)}
                      ref={values.identityProvider === option.value ? providerFieldRef : undefined}
                      className="accent-primary"
                      disabled={submitting}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Microsoft 365 and Google Workspace let colleagues on the domain join automatically.
                Other means the invitation is the only way in.
              </p>
            </fieldset>

            <TextField
              field="workEmailDomain"
              id="create-org-domain"
              label="Work email domain"
              value={values.workEmailDomain}
              error={fieldErrors.workEmailDomain}
              inputRef={fieldRefs?.workEmailDomain}
              onChange={(value) => onChange('workEmailDomain', value)}
              placeholder="company.example"
              autoCapitalize="none"
              spellCheck={false}
              disabled={submitting}
            />

            <TextField
              field="billingEmail"
              id="create-org-billing-email"
              label="Billing email"
              type="email"
              value={values.billingEmail}
              error={fieldErrors.billingEmail}
              inputRef={fieldRefs?.billingEmail}
              onChange={(value) => onChange('billingEmail', value)}
              placeholder="ceo@company.example"
              disabled={submitting}
            />

            {retrying !== null && (
              <div role="status" className="text-sm text-muted-foreground">
                Another create for this domain is still running… retrying (attempt {retrying} of {CREATE_IN_PROGRESS_RETRY_ATTEMPTS}).
              </div>
            )}

            {failure && (
              <div role="alert" className={ALERT_CLASS}>
                <span>{failure.message}</span>
                {failure.kind === 'conflict' && failure.matches.map((match) => (
                  <a
                    key={match.id}
                    className={buttonVariants({ variant: 'link', size: 'sm' })}
                    href={getOrgDetailUrl(match.id)}
                  >
                    {failure.matches.length === 1 ? 'Open' : `Open ${match.displayName}`}
                  </a>
                ))}
                {failure.kind === 'ambiguous' && failure.organizationId && (
                  <a
                    className={buttonVariants({ variant: 'link', size: 'sm' })}
                    href={getOrgDetailUrl(failure.organizationId)}
                  >
                    Open
                  </a>
                )}
                {failure.kind === 'in_progress' && (
                  <Button type="button" variant="ghost" size="sm" onClick={onRetry}>Retry</Button>
                )}
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Nothing is sent. Add staff, enable Trainer and build the account first; invite the
              customer from the Users tab when you are ready.
            </p>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form="create-org-form" disabled={submitting}>
            {submitting ? (retrying !== null ? 'Waiting…' : 'Creating…') : 'Create organization'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

export function CreateOrganizationDialog({ onClose }: { onClose: () => void }) {
  const [values, setValues] = useState<CreateOrganizationFormValues>(EMPTY_CREATE_ORGANIZATION_FORM);
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<CreateOrganizationFieldErrors>({});
  const [failure, setFailure] = useState<CreateOrganizationFailure | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState<number | null>(null);
  // Idempotency key for the create (spec §6.1), bound to the normalized payload it was first sent
  // with: the backend replays by key alone and returns the original org without comparing
  // payloads, so an unchanged retry reuses the key and any edit mints a new one. Kept across a
  // 502 / network retry; dropped after any 4xx, including a 409 request_in_progress whose
  // retries ran out (createWithInProgressRetry already reused it through its attempts).
  const [submission, setSubmission] = useState<CreateSubmission | null>(null);
  const providerFieldRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const domainRef = useRef<HTMLInputElement>(null);
  const billingEmailRef = useRef<HTMLInputElement>(null);
  const fieldRefs: CreateOrganizationFieldRefs = {
    name: nameRef,
    workEmailDomain: domainRef,
    billingEmail: billingEmailRef,
  };

  useDialogEscape(onClose, !submitting);

  useEffect(() => {
    if (failure?.kind === 'not_microsoft_tenant') providerFieldRef.current?.focus();
  }, [failure]);

  const focusField = (field: keyof CreateOrganizationFormValues | null) => {
    if (field === 'identityProvider') {
      providerFieldRef.current?.focus();
    } else if (field === 'name' || field === 'workEmailDomain' || field === 'billingEmail') {
      fieldRefs[field]?.current?.focus();
    }
  };

  // A field the server refused is focused once the submit has ended: the inputs are disabled while
  // it runs, so focusing in the catch would be lost.
  const refusedFieldRef = useRef<keyof CreateOrganizationFormValues | null>(null);
  useEffect(() => {
    if (submitting || refusedFieldRef.current === null) return;
    focusField(refusedFieldRef.current);
    refusedFieldRef.current = null;
  }, [submitting]);

  const handleChange = <K extends keyof CreateOrganizationFormValues>(
    field: K,
    value: CreateOrganizationFormValues[K],
  ) => {
    setValues((prev) => {
      const next = { ...prev, [field]: value } as CreateOrganizationFormValues;
      if (field === 'name') {
        next.displayName = nextDisplayName(value as string, prev.displayName, displayNameTouched);
      }
      return next;
    });
    setFieldErrors((prev) => clearFieldError(prev, field));
    if (field === 'displayName') setDisplayNameTouched(true);
  };

  const handleSubmit = async () => {
    const validation = validateCreateOrganizationForm(values);
    if (!validation.ok) {
      setFieldErrors(validation.errors);
      focusField(firstInvalidField(validation.errors));
      return;
    }
    const current = submissionFor(submission, validation.request, () => crypto.randomUUID());
    setSubmission(current);
    setFieldErrors({});
    setFailure(null);
    setSubmitting(true);
    setRetrying(null);
    try {
      // 409 request_in_progress: another create with this key or domain is still running, so wait
      // 2 s and present the SAME key again, five attempts in all. A 500
      // organization_create_failed needs nothing beyond keeping the key: without organizationId
      // the server released its claims and the same-key retry creates afresh; with organizationId
      // it kept the requestId claim and the same-key retry replays the existing organization.
      const org = await createWithInProgressRetry(
        (id) => adminApi.createOrganization(validation.request, id),
        current.requestId,
        (failedAttempt) => setRetrying(failedAttempt + 1),
      );
      window.location.href = getOrgDetailUrl(org.id, { tab: 'users' });
    } catch (err: unknown) {
      const { status, code } = readApiFailure(err);
      const mapped = mapCreateOrganizationFailure(err, normalizeWorkEmailDomain(values.workEmailDomain));
      if (mapped.kind === 'field') {
        setFieldErrors({ [mapped.field]: mapped.message });
        refusedFieldRef.current = mapped.field;
      } else {
        setFailure(mapped);
      }
      if (!keepRequestIdAfterFailure(status, code)) setSubmission(null);
      setRetrying(null);
      setSubmitting(false);
    }
  };

  return (
    <CreateOrganizationDialogView
      values={values}
      fieldErrors={fieldErrors}
      failure={failure}
      submitting={submitting}
      retrying={retrying}
      onChange={handleChange}
      onSubmit={() => void handleSubmit()}
      onRetry={() => void handleSubmit()}
      onClose={onClose}
      fieldRefs={fieldRefs}
      providerFieldRef={providerFieldRef}
    />
  );
}
